/*----------------------------------
- WEBSITE TO PRODUCT HANDOFF DECISIONS
----------------------------------*/

/**
 * @fix docs/fixes/2026-08-13-website-login-handoff-expired-link.md
 * @fix docs/fixes/2026-10-20-settings-link-lost-after-sign-in.md
 * @rule An authenticated member who already completed onboarding is handed to the
 *       product directly. Never call BeginConversion for them, and never render its
 *       refusal as a failure.
 * @rule A `returnTo` on Browse, on `/brief` or on a team invitation is the landing; any other
 *       Product path lands on `/domains` (ADR-0083, ADR-0109 decision 26).
 */

import { InputError } from '@common/errors';
import { ONBOARDING_PROGRESS_COMPLETED_ERROR_ID } from '@uniquedomains/components/contracts/conversion/errors';
import {
    isConversionLiveSessionUsable,
    type CurrentUserSession,
} from '@uniquedomains/components/contracts/users/session';
import { PRODUCT_PAGE_ROUTES, PRODUCT_TEAM_INVITE_PATH } from '@/common/catalogs/appRoutesCatalog';

/*----------------------------------
- HELPERS
----------------------------------*/

/** The exact path, or the path followed by a query or a hash (and a child page when allowed). */
const isProductPathOrChild = (returnTo: string, path: string, { childPages }: { childPages: boolean }): boolean =>
    returnTo === path
    || returnTo.startsWith(`${path}?`)
    || returnTo.startsWith(`${path}#`)
    || (childPages && returnTo.startsWith(`${path}/`));

/*----------------------------------
- PUBLIC API
----------------------------------*/

/**
 * Where the browser is sent once the account exists.
 *
 * A `returnTo` is followed when it points at Browse or at `/brief`, the two pages an
 * account is sent to. Since ADR-0083 the onboarding modal lives on `/brief` and Product's
 * paid-access pin moves the account on from there, so a settings link
 * (`/brief?section=security`) reopens its section after sign-in. A team invitation
 * (`/invite/team/<token>`) is followed too: it sits outside the pin, and its acceptance is
 * what lets the new teammate in (Gaetan's decision of 2026-10-30, ADR-0109 decision 26: the
 * invitation link must not be lost after Switch account). Any other Product path lands on
 * `/domains`: following every path would move the landing of every wall's `returnTo`, a
 * wider conversion change. The full `returnTo` still travels to the product with the
 * handoff, which lands there once setup is finished.
 */
export const resolveProductLandingPath = (returnTo: string | undefined): string => {
    if (!returnTo) return PRODUCT_PAGE_ROUTES.domains;
    const isFollowedPath = isProductPathOrChild(returnTo, PRODUCT_PAGE_ROUTES.domains, { childPages: true })
        || isProductPathOrChild(returnTo, PRODUCT_PAGE_ROUTES.brief, { childPages: false })
        || returnTo.startsWith(`${PRODUCT_TEAM_INVITE_PATH}/`);
    return isFollowedPath ? returnTo : PRODUCT_PAGE_ROUTES.domains;
};

/**
 * A live session that already finished onboarding needs the redirect and nothing else:
 * its conversion flow is closed, so asking the product to open one is both pointless
 * and refused. `onboardingDate` is the right signal because Product writes it in the
 * same transaction that completes the flow, so the two can never disagree.
 */
export const isWorkspaceReadySession = (session: CurrentUserSession | null): boolean =>
    isConversionLiveSessionUsable(session) && Boolean(session.onboardingDate);

/**
 * The product's "already completed" refusal, recognised so the handoff can treat it as
 * the expected answer for a returning member rather than an error. Kept as a fallback
 * behind `isWorkspaceReadySession`: it also covers a session read that raced or failed.
 */
export const isOnboardingAlreadyCompletedError = (error: unknown): error is InputError =>
    error instanceof InputError && error.details.id === ONBOARDING_PROGRESS_COMPLETED_ERROR_ID;
